/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { proxyAPI } from "../proxyAPI.ts";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import MyPagination from "../Common/MyPagination";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import ContactCard from "../Contacts/ContactCard";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import ReportingContactsList from "./ReportingContactsList";

export const ContactPopover = React.forwardRef(
  ({ popper, children, show: _, ...props }, ref) => {
    const [contact, setContact] = useState(null);

    const getContact = async () => {
      const data = await proxyAPI({
        prefix: "contacts/v1",
        url: `/first/${props.uuid}?fields.cards=1&fields.channels=1`,
      });
      setContact(data);
    };

    useEffect(() => {
      getContact();
    }, []);

    const contactFields =
      contact?.details
        .filter((d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice)
        .filter((d) => d.values[0].data || d.values[0].option.label)
        .slice(0, 2)
        .map((d) => d.field) ?? [];

    useEffect(() => {
      popper.scheduleUpdate();
    }, [contact, popper]);

    return (
      <Popover ref={ref} body {...props}>
        <div className="overflow-scroll">
          <table>
            <thead>
              <tr>
                <th className="px-2" />
                <th className="px-2" />

                {contactFields.map((cf) => (
                  <th className="px-2" key={cf.uuid}>
                    {cf.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contact && (
                <ContactCard
                  contact={contact}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  contactButton={() => (
                    <Link to={`/assets/contacts/edit/${props.uuid}`}>
                      <FontAwesomeIcon icon={faPen} />
                    </Link>
                  )}
                  contactFields={contactFields}
                  fullView={false}
                />
              )}
            </tbody>
          </table>
        </div>
      </Popover>
    );
  }
);

export function contactOption({ selectOption, ...props }) {
  return (
    <div className="border-bottom">
      <table>
        <tbody>
          <ContactCard
            contact={props.data.contact}
            contactFields={props.data.contact.details
              .filter(
                (d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice
              )
              .filter((d) => d.values[0].data || d.values[0].option.label)
              .slice(0, 2)
              .map((d) => d.field)}
            contactButton={() => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                type="button"
                className="btn btn-outline-primary border-0"
                onClick={() => selectOption(props.data)}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          />
        </tbody>
      </table>
    </div>
  );
}

export function contactMenuList(props) {
  return (
    <div style={{ ...props.style, height: "100%", overflow: "auto" }}>
      <table className="table table-condensed bg-white">
        <tbody>{props.children}</tbody>
      </table>
    </div>
  );
}

export async function loadContactOptions(search, prevOptions) {
  const data = await proxyAPI({
    prefix: "contacts/v1",
    url: `find`,
    method: "POST",
    body: {
      filters: [
        {
          values: [search],
          is_or: true,
          fields: [],
          formats: ["card", "primary"],
          order: 0,
          operator: "prefix",
        },
      ],
      paginate: {
        order: "desc",
        order_by: "created_at",
        page: prevOptions.length / 10,
        perPage: 10,
      },
      fields: { uuids: [], cards: true, channels: true },
    },
  });

  return {
    options: data.contacts.map((cs) => {
      const value = cs.details.sort((a, b) => a.field.order - b.field.order)[0]
        .values[0];
      return {
        value: cs.uuid,
        label: value?.option?.label ?? value?.data ?? cs.uuid,
        contact: cs,
      };
    }),
    hasMore: data.pagination.total > prevOptions.length + data.contacts.length,
  };
}

export default function TicketReportingOthers() {
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]);
  const [filterTemplates, setFilterTemplates] = useState([]);
  const [filterContacts, setFilterContacts] = useState([]);
  const [filterTickets, setFilterTickets] = useState(
    location.state?.ticket ? [location.state?.ticket] : []
  );
  const [filterFields, setFilterFields] = useState([]);
  const [dateLTE, setDateLTE] = useState(null);
  const [dateGTE, setDateGTE] = useState(null);

  const getEntries = async (page = 0, perPage = 10) => {
    setLoading(true);
    const body = {
      pagination: {
        page,
        perPage,
      },
      filters: {
        topics: filterTopics,
        tickets: filterTickets,
        contactUuids: filterContacts,
        templateUuids: filterTemplates,
        search: "",
        dateGte: dateGTE,
        dateLte: dateLTE,
        // uuids: entries,
        values: filterFields,
      },
    };
    const data = await proxyAPI({
      prefix: "logic/reporting",
      url: `get-entries`,
      method: "POST",
      body,
    });
    setTimeout(() => {
      setLoading(false);
      if (data?.data) {
        setEntries(data.data);
        setTotal(data.info.total);
      }
    }, 500);
  };

  useEffect(() => {
    (async () => {
      const topicsData = await proxyAPI({
        prefix: "logic/reporting",
        url: `topics`,
      });
      if (topicsData) {
        setTopics(topicsData.data);
      }
      const templatesData = await proxyAPI({
        prefix: "logic/reporting",
        url: `templates`,
      });
      if (templatesData) {
        setTemplates(templatesData.data);
      }
    })();
  }, []);

  const selectedTemplateFields = templates
    .filter((t) => filterTemplates.includes(t.uuid))
    .flatMap((t) => t.fields.map((f) => ({ ...f, template: t })));

  return (
    <div>
      <FullScreenLoadingIndicator loading={loading} />
      <div className="">
        <div className="row mb-3 bg-colored-reverse rounded-3 p-3 mx-0">
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Topics
            </label>
            <Input
              inputType="select"
              isMulti
              placeholder="Select topics"
              isValidNewOption={() => false}
              value={filterTopics}
              onChange={(v) => setFilterTopics(v)}
              options={topics.map((t) => ({ value: t.uuid, label: t.label }))}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Templates
            </label>
            <Input
              inputType="select"
              isMulti
              placeholder="Select templates"
              isValidNewOption={() => false}
              value={filterTemplates}
              onChange={(v) => {
                setFilterFields(
                  filterFields.filter((f) =>
                    templates.some(
                      (t) =>
                        v.includes(t.uuid) &&
                        t.fields.some((f2) => f.fields?.includes(f2.uuid))
                    )
                  )
                );
                setFilterTemplates(v);
              }}
              options={templates.map((t) => ({
                value: t.uuid,
                label: t.label,
              }))}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Tickets
            </label>
            <Input
              isMulti
              isValidNewOption={(v) => v.trim().length > 0}
              placeholder="Enter tickets"
              value={filterTickets}
              onChange={(v) => setFilterTickets(v)}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Contacts
            </label>
            <Input
              isMulti
              placeholder="Select contacts"
              value={filterContacts}
              onChange={(v) => setFilterContacts(v)}
              isValidNewOption={() => false}
              loadOptions={loadContactOptions}
              components={{
                Option: contactOption,
                //  , MenuList: contactMenuList
              }}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Date after
            </label>
            <Input
              inputType="dateTime"
              dateTimeType="dateTime"
              isClearable
              value={[dateGTE]}
              onChange={(v) => setDateGTE(v[0])}
            />
          </div>
          <div className="col-md-2">
            <label htmlFor="" className="text-white fw-bold p-2">
              Date before
            </label>
            <Input
              inputType="dateTime"
              dateTimeType="dateTime"
              isClearable
              value={[dateLTE]}
              onChange={(v) => setDateLTE(v[0])}
            />
          </div>
          {filterTemplates.length > 0 && (
            <div className="col-12 mt-2">
              <div className="text-white mb-1">
                Filter by field values{" "}
                <button
                  type="button"
                  className="btn btn-outline-light border-0 btn-sm ms-3"
                  onClick={() =>
                    setFilterFields([
                      ...filterFields,
                      { or: false, fields: [], operator: "is", values: [] },
                    ])
                  }
                >
                  <FontAwesomeIcon icon={faPlus} /> Add field filter
                </button>
              </div>
              <div className="">
                {filterFields.map((f, i) => {
                  const selectedField = selectedTemplateFields.find(
                    (fld) => fld.uuid === filterFields[i].fields[0]
                  );
                  console.log(selectedField);
                  return (
                    <div className="d-flex mb-2" key={i}>
                      {i === 0 ? null : (
                        <div className="me-2">
                          <Input
                            inputType="select"
                            value={[f.or]}
                            onChange={(v) => {
                              const newFilterFields = [...filterFields];
                              newFilterFields[i].logicalOperator = v[0];
                              setFilterFields(newFilterFields);
                            }}
                            options={[
                              { value: false, label: "And" },
                              { value: true, label: "Or" },
                            ]}
                          />
                        </div>
                      )}
                      <div className="me-2">
                        <Input
                          placeholder="Select field"
                          inputType="select"
                          value={f.fields ? f.fields : []}
                          onChange={(v) => {
                            const newFilterFields = [...filterFields];
                            newFilterFields[i].fields = v;
                            setFilterFields(newFilterFields);
                          }}
                          isValidNewOption={() => false}
                          options={selectedTemplateFields.map((fld) => ({
                            value: fld.uuid,
                            label:
                              fld.template.label +
                              " - " +
                              fld.label +
                              " (" +
                              fld.type +
                              ")",
                          }))}
                        />
                      </div>
                      <div className="me-2">
                        <Input
                          inputType="select"
                          value={f.operator ? [f.operator] : []}
                          onChange={(v) => {
                            const newFilterFields = [...filterFields];
                            newFilterFields[i].operator = v[0];
                            newFilterFields[i].values = [];
                            setFilterFields(newFilterFields);
                          }}
                          options={[
                            { value: "is", label: "Is" },
                            { value: "is-not", label: "Is not" },
                            ...(["NUMBER", "TIME", "DURATION"].includes(
                              selectedField?.type
                            )
                              ? [
                                  { value: "lte", label: "Less than or equal" },
                                  {
                                    value: "gte",
                                    label: "Greater than or equal",
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </div>
                      <div className="me-2">
                        <Input
                          placeholder="Enter value"
                          value={f.values ? f.values : []}
                          isMulti={
                            !["NUMBER", "TIME", "DURATION"].includes(
                              selectedField?.type
                            )
                          }
                          inputType={selectedField?.type === "CATEGORY" ? "select" : undefined}
                          options={
                            selectedField?.type === "CATEGORY"
                              ? selectedField.categories.map((o) => ({
                                  value: o,
                                  label: o,
                                }))
                              : []
                          }
                          isValidNewOption={(v) => v.trim().length > 0}
                          isClearable
                          onChange={(v) => {
                            const newFilterFields = [...filterFields];
                            newFilterFields[i].values = v;
                            setFilterFields(newFilterFields);
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-light border-0"
                        onClick={() => {
                          const newFilterFields = [...filterFields];
                          newFilterFields.splice(i, 1);
                          setFilterFields(newFilterFields);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="mb-5 bg-white rounded-3 p-3">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="p-3">Created at</th>
                  <th className="p-3">Topic</th>
                  <th className="p-3">Ticket</th>
                  <th className="p-3">Template</th>
                  <th className="p-3">Values</th>
                  <th className="p-3 text-center">Contacts</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.uuid}>
                    <td className="p-3">
                      {new Date(
                        e.createdAt.slice(-1) === "Z"
                          ? e.createdAt
                          : `${e.createdAt}Z`
                      ).toLocaleString()}
                    </td>
                    <td className="p-3">{e.topic.label}</td>
                    <td className="p-3">{e.ticket.uuid || "N/A"}</td>
                    <td className="p-3">{e.template.label}</td>
                    <td className="p-3">
                      {e.values.map((v) => (
                        <span
                          className="me-2"
                          key={`${v.data}-${v.field.uuid}`}
                        >
                          <span className="text-secondary">
                            {e.template.fields.find(
                              (f) => f.uuid === v.field.uuid
                            )?.label ?? "unknown"}
                          </span>
                          : {v.data}
                        </span>
                      ))}
                    </td>
                    <td className="p-3 text-center">
                      {e.meta.contactsCount ? (
                        <ReportingContactsList
                          label={e.meta.contactsCount}
                          uuid={e.uuid}
                          kind="entry"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <MyPagination
        total={total}
        onPaginate={getEntries}
        key={[
          filterTopics,
          filterTemplates,
          filterTickets,
          filterContacts,
          JSON.stringify(filterFields),
          dateGTE,
          dateLTE,
        ]}
      />
      <JsonCollapse json={entries} />
    </div>
  );
}
